import React, { FC, useMemo } from "react";
import Dialog from "../../Unknown/Dialog";
import DialogTitle from "../../Unknown/DialogTitle";
import DialogContent from "../../Unknown/DialogContent";
import DialogActions from "../../Unknown/DialogActions";
import Button from "../../Unknown/Button";
import Typography from "../../Unknown/Typography";
import Box from "../../Unknown/Box";
import Chip, { ChipProps } from "../../Unknown/Chip";
import useTranslations from "./useTranslations";
import { RichTextContent } from "../../Unknown/RichTextContent";
import {
  AiReportSection,
  FindingSeverity,
} from "../AppointmentReportGrid/types";

export type AppointmentReportAiReportViewDialogProps = {
  isOpened: boolean;
  sections: AiReportSection[];
  customerName: string;
  locationCity: string;
  studyNames: string[];
  appointmentDate: string;
  handleClose: () => void;
};

const AppointmentReportAiReportViewDialog: FC<
  AppointmentReportAiReportViewDialogProps
> = ({
  isOpened,
  sections,
  customerName,
  locationCity,
  studyNames,
  appointmentDate,
  handleClose,
}) => {
  const { translations } = useTranslations({
    customerName,
    appointmentDate,
    locationCity,
    studyNames,
  });

  const actionButtons = useMemo(() => {
    return [
      {
        text: translations.btnClose,
        key: "close",
        color: "primary" as const,
        onClick: handleClose,
      },
    ];
  }, [translations.btnClose, handleClose]);

  const getSeverityChipProps = (
    severity: FindingSeverity,
  ): { label: string; color: ChipProps["color"] } => {
    switch (severity) {
      case "action_required":
        return {
          label: "action required",
          color: "error",
        };
      case "no_action_required":
        return {
          label: "no action required",
          color: "success",
        };
      case "requires_attention":
        return {
          label: "requires attention",
          color: "warning",
        };
      default:
        return {
          label: "no action required",
          color: "success",
        };
    }
  };

  return (
    <Dialog
      open={isOpened}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          maxWidth: 760,
        },
      }}
    >
      <DialogTitle>
        {translations.title}
        <Typography variant="body2" color="text.secondary">
          {translations.subtitle}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3}>
          {sections.map((section, sectionIndex) => {
            const hasContent =
              section.text ||
              (section.findings && section.findings.length > 0) ||
              (section.nextSteps && section.nextSteps.length > 0);

            if (!hasContent) return null;

            return (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={`${section.title}-${sectionIndex}`}
                display="flex"
                flexDirection="column"
                p={4}
                borderRadius={1}
                bgcolor="grey.100"
                gap={4}
              >
                <Typography variant="h3" component="h3">
                  {translations.getSectionTitle(section.title)}
                </Typography>

                {/* Display text content (for summary and warnings) */}
                {section.text && (
                  <Box bgcolor="common.white" p={3} borderRadius={1}>
                    <RichTextContent content={section.text} fontSize="16px" />
                  </Box>
                )}

                {/* Display findings array */}
                {section.findings && section.findings.length > 0 && (
                  <Box display="flex" flexDirection="column" gap={4}>
                    {section.findings.map((finding, index) => {
                      const chipProps = getSeverityChipProps(finding.severity);
                      return (
                        <Box
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${finding.title}-${index}`}
                          p={3}
                          bgcolor="common.white"
                          borderRadius={1}
                        >
                          <Box display="flex" alignItems="center" gap={6}>
                            {finding.title && (
                              <Typography variant="body1">
                                {finding.title}
                              </Typography>
                            )}
                            <Chip
                              label={chipProps.label}
                              size="small"
                              color={chipProps.color}
                            />
                          </Box>
                          <Box mt={3}>
                            <Typography variant="body2" display="inline">
                              What This Means:
                            </Typography>
                            <Typography ml={1} variant="body2" display="inline">
                              {finding.whatThisMeans}
                            </Typography>
                          </Box>
                          <Box mt={3}>
                            <Typography variant="body2" display="inline">
                              Why It Matters:
                            </Typography>
                            <Typography ml={1} variant="body2" display="inline">
                              {finding.whyItMatters}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Display next steps array */}
                {section.nextSteps && section.nextSteps.length > 0 && (
                  <Box display="flex" flexDirection="column" gap={4}>
                    {section.nextSteps.map((step, index) => (
                      <Box
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${step.title}-${index}`}
                        bgcolor="common.white"
                        p={3}
                        borderRadius={1}
                      >
                        <Typography variant="body1">{step.title}</Typography>
                        <Typography variant="body2" mt={3}>
                          {step.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        {actionButtons.map(({ text, key, ...props }) => (
          <Button size="small" {...props} key={key}>
            {text}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentReportAiReportViewDialog;
